import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { api } from '@/api';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Calendar, Users, Clock, AlertTriangle,
  CheckCircle2, Loader2, Plus, Wand2, ListTodo, Building2, User,
  AlertCircle, Download, Eye, Trash2, RefreshCw, ChevronRight,
  FileQuestion, Send, ExternalLink, XCircle, CheckCircle, Edit3,
  Target, TrendingUp, Lightbulb, Flag, Zap, Mail
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const PRIORITY_COLORS = {
  'Low': 'bg-slate-100 text-slate-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-amber-100 text-amber-700',
  'Urgent': 'bg-red-100 text-red-700'
};

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  'Awaiting Response': 'bg-purple-100 text-purple-700',
  'Blocked': 'bg-red-100 text-red-700',
  'Closed': 'bg-green-100 text-green-700',
  'Overdue': 'bg-red-100 text-red-700'
};

const MeetingMinutesDetail = () => {
  const { minutesId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState(null);
  const [actions, setActions] = useState([]);
  const [activeTab, setActiveTab] = useState('summary');
  
  // Action creation
  const [selectedActions, setSelectedActions] = useState([]);
  const [creatingActions, setCreatingActions] = useState(false);
  
  // Re-extraction
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    loadMinutes();
    loadActions();
  }, [minutesId]);

  const loadMinutes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/meeting-minutes/${minutesId}`);
      setMinutes(response.data);
    } catch (error) {
      toast.error('Failed to load minutes');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      const response = await api.get(`/meeting-minutes/${minutesId}/actions`);
      setActions(response.data || []);
    } catch (error) {
      console.error('Failed to load actions:', error);
    }
  };

  const handleExtract = async () => {
    setExtracting(true);
    try {
      await api.post(`/meeting-minutes/${minutesId}/extract`);
      toast.success('AI extraction completed');
      loadMinutes();
    } catch (error) {
      toast.error('Extraction failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExtracting(false);
    }
  };

  const handleConfirmExtraction = async () => {
    try {
      await api.post(`/meeting-minutes/${minutesId}/confirm-extraction`, minutes.extraction_result);
      toast.success('Extraction confirmed');
      loadMinutes();
    } catch (error) {
      toast.error('Failed to confirm extraction');
    }
  };

  const handleCreateActions = async () => {
    if (selectedActions.length === 0) {
      toast.error('Please select actions to create');
      return;
    }

    setCreatingActions(true);
    try {
      const actionsToCreate = selectedActions.map(action => ({
        minutes_id: minutesId,
        task_title: action.description,
        task_description: action.source_text || action.description,
        assigned_to_party: mapPartyType(action.assigned_party),
        assigned_to_name: action.assigned_to,
        due_date: parseDueDate(action.due_date),
        priority: action.priority || 'Medium',
        tags: action.cost_impact || action.time_impact ? 
          [action.cost_impact && 'Cost Impact', action.time_impact && 'Time Impact'].filter(Boolean) : [],
        source_text: action.source_text
      }));

      await api.post(`/meeting-minutes/${minutesId}/actions/bulk`, actionsToCreate);
      toast.success(`Created ${actionsToCreate.length} actions`);
      setSelectedActions([]);
      loadActions();
    } catch (error) {
      toast.error('Failed to create actions');
    } finally {
      setCreatingActions(false);
    }
  };

  const mapPartyType = (party) => {
    const mapping = {
      'Contractor': 'Contractor',
      'Consultant': 'Consultant',
      'Subcontractor': 'Subcontractor',
      'Client': 'Client',
      'Internal': 'Internal'
    };
    return mapping[party] || 'Contractor';
  };

  const parseDueDate = (dueDateStr) => {
    if (!dueDateStr) return null;
    try {
      const date = new Date(dueDateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    } catch {}
    return null;
  };

  const handleConvertToRFI = async (actionId) => {
    try {
      const response = await api.post(`/meeting-minutes/actions/${actionId}/convert-to-rfi`);
      toast.success('RFI created from action');
      navigate(`/rfis/${response.data.rfi.id}`);
    } catch (error) {
      toast.error('Failed to create RFI');
    }
  };

  const handleConvertToDelayEvent = async (actionId) => {
    try {
      const response = await api.post(`/meeting-minutes/actions/${actionId}/convert-to-delay-event`);
      toast.success('Delay Event created from action');
      navigate(`/delay-events/${response.data.delay_event.id}`);
    } catch (error) {
      toast.error('Failed to create Delay Event');
    }
  };

  const handleUpdateActionStatus = async (actionId, newStatus) => {
    try {
      await api.put(`/meeting-minutes/actions/${actionId}`, { status: newStatus });
      toast.success('Action updated');
      loadActions();
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  const toggleActionSelection = (action) => {
    const isSelected = selectedActions.some(a => a.description === action.description);
    if (isSelected) {
      setSelectedActions(selectedActions.filter(a => a.description !== action.description));
    } else {
      setSelectedActions([...selectedActions, action]);
    }
  };

  const selectAllActions = () => {
    const extractedActions = minutes?.extraction_result?.actions || [];
    if (selectedActions.length === extractedActions.length) {
      setSelectedActions([]);
    } else {
      setSelectedActions([...extractedActions]);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!minutes) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <p className="text-slate-500">Minutes not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const extraction = minutes.extraction_result || {};

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start sm:items-center gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="rounded-sm flex-shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-barlow font-bold uppercase tracking-wider text-slate-900 line-clamp-2">
                {minutes.meeting_title}
              </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
                <Badge variant="outline" className="rounded-sm text-xs">
                  {minutes.meeting_type}
                </Badge>
                <span className="text-xs sm:text-sm text-slate-500">
                  <Calendar className="h-3 w-3 inline mr-1" />
                  {formatDate(minutes.meeting_date)}
                </span>
                {minutes.chairperson && (
                  <span className="text-xs sm:text-sm text-slate-500 hidden sm:inline">
                    <User className="h-3 w-3 inline mr-1" />
                    {minutes.chairperson}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-2 ml-8 sm:ml-0">
            {minutes.minutes_file_url && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
                  const downloadUrl = `${apiUrl}${minutes.minutes_file_url}`;
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = minutes.minutes_file_name || 'minutes.pdf';
                  link.target = '_blank';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="rounded-sm text-xs sm:text-sm"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Download</span>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExtract}
              disabled={extracting}
              className="rounded-sm text-xs sm:text-sm"
            >
              {extracting ? (
                <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">Re-Extract</span>
            </Button>
            {!minutes.extraction_confirmed && extraction.actions?.length > 0 && (
              <Button
                onClick={handleConfirmExtraction}
                size="sm"
                className="rounded-sm bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
              >
                <CheckCircle2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Confirm</span>
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
            <TabsList className="bg-slate-100 p-1 rounded-sm inline-flex min-w-max">
              <TabsTrigger value="summary" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-3">
                <Lightbulb className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Summary</span>
              </TabsTrigger>
              <TabsTrigger value="actions" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-3">
                <ListTodo className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Actions</span> ({extraction.actions?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="issues" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-3">
                <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Issues</span> ({extraction.issues?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="risks" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-3">
                <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Risks</span> ({extraction.risks?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="tracked" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-3">
                <Target className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Tracked</span> ({actions.length})
              </TabsTrigger>
              <TabsTrigger value="correspondence" className="rounded-sm data-[state=active]:bg-white text-xs sm:text-sm px-2 sm:px-3">
                <Mail className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                <span className="hidden sm:inline">Correspondence</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Executive Summary */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-500" />
                    Executive Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {extraction.executive_summary?.length > 0 ? (
                    <ul className="space-y-2">
                      {extraction.executive_summary.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No summary available</p>
                  )}
                </CardContent>
              </Card>

              {/* Key Decisions */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Key Decisions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {extraction.key_decisions_summary?.length > 0 ? (
                    <ul className="space-y-2">
                      {extraction.key_decisions_summary.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No decisions extracted</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Risks */}
              <Card className="border-red-200 bg-red-50/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-red-600 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Top Risks
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {extraction.top_risks_summary?.length > 0 ? (
                    <ul className="space-y-2">
                      {extraction.top_risks_summary.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <Flag className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No risks identified</p>
                  )}
                </CardContent>
              </Card>

              {/* Immediate Actions */}
              <Card className="border-amber-200 bg-amber-50/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-600 flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Immediate Actions Required
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {extraction.immediate_actions_summary?.length > 0 ? (
                    <ul className="space-y-2">
                      {extraction.immediate_actions_summary.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                          <Zap className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No immediate actions identified</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Potential Delay Drivers & Variations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-purple-500" />
                    Potential Delay Drivers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {extraction.potential_delay_drivers?.length > 0 ? (
                    <ul className="space-y-1">
                      {extraction.potential_delay_drivers.map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-purple-500 font-bold">{idx + 1}.</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">None identified</p>
                  )}
                </CardContent>
              </Card>

              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    Potential Variations / Claims
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {extraction.potential_variations?.length > 0 ? (
                    <ul className="space-y-1">
                      {extraction.potential_variations.map((item, idx) => (
                        <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                          <span className="text-blue-500 font-bold">{idx + 1}.</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">None identified</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Actions Tab */}
          <TabsContent value="actions" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">
                  Extracted Action Items
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllActions}
                    className="rounded-sm"
                  >
                    {selectedActions.length === (extraction.actions?.length || 0) ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleCreateActions}
                    disabled={selectedActions.length === 0 || creatingActions}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    {creatingActions ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Tasks ({selectedActions.length})
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {extraction.actions?.length > 0 ? (
                  <div className="space-y-3">
                    {extraction.actions.map((action, idx) => {
                      const isSelected = selectedActions.some(a => a.description === action.description);
                      return (
                        <div
                          key={idx}
                          className={`border rounded-sm p-4 cursor-pointer transition-colors ${
                            isSelected ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => toggleActionSelection(action)}
                        >
                          <div className="flex items-start gap-3">
                            <Checkbox checked={isSelected} className="mt-1" />
                            <div className="flex-1">
                              <p className="font-medium text-slate-900">{action.description}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                {action.assigned_to && (
                                  <Badge variant="outline" className="text-xs">
                                    <User className="h-3 w-3 mr-1" />
                                    {action.assigned_to}
                                  </Badge>
                                )}
                                {action.assigned_party && (
                                  <Badge variant="outline" className="text-xs">
                                    <Building2 className="h-3 w-3 mr-1" />
                                    {action.assigned_party}
                                  </Badge>
                                )}
                                {action.due_date && (
                                  <Badge variant="outline" className="text-xs">
                                    <Calendar className="h-3 w-3 mr-1" />
                                    {action.due_date}
                                  </Badge>
                                )}
                                {action.priority && (
                                  <Badge className={`text-xs ${PRIORITY_COLORS[action.priority] || ''}`}>
                                    {action.priority}
                                  </Badge>
                                )}
                                {action.cost_impact && (
                                  <Badge className="text-xs bg-red-100 text-red-700">Cost Impact</Badge>
                                )}
                                {action.time_impact && (
                                  <Badge className="text-xs bg-amber-100 text-amber-700">Time Impact</Badge>
                                )}
                              </div>
                              {action.source_text && (
                                <p className="text-xs text-slate-500 mt-2 italic">
                                  Source: "{action.source_text.substring(0, 150)}..."
                                </p>
                              )}
                            </div>
                            <Badge className="text-xs bg-slate-100 text-slate-600">
                              {Math.round((action.confidence || 0.8) * 100)}%
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic py-8 text-center">
                    No action items extracted. Try re-running extraction.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issues Tab */}
          <TabsContent value="issues" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">
                  Extracted Issues
                </CardTitle>
              </CardHeader>
              <CardContent>
                {extraction.issues?.length > 0 ? (
                  <div className="space-y-3">
                    {extraction.issues.map((issue, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-sm p-4">
                        <p className="font-medium text-slate-900">{issue.description}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {issue.severity && (
                            <Badge className={`text-xs ${
                              issue.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                              issue.severity === 'High' ? 'bg-amber-100 text-amber-700' :
                              issue.severity === 'Medium' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {issue.severity}
                            </Badge>
                          )}
                          {issue.area && (
                            <Badge variant="outline" className="text-xs">{issue.area}</Badge>
                          )}
                          {issue.responsible_party && (
                            <Badge variant="outline" className="text-xs">
                              <Building2 className="h-3 w-3 mr-1" />
                              {issue.responsible_party}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic py-8 text-center">
                    No issues extracted
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risks Tab */}
          <TabsContent value="risks" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">
                  Extracted Risks
                </CardTitle>
              </CardHeader>
              <CardContent>
                {extraction.risks?.length > 0 ? (
                  <div className="space-y-3">
                    {extraction.risks.map((risk, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-sm p-4">
                        <p className="font-medium text-slate-900">{risk.description}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {risk.likelihood && (
                            <Badge variant="outline" className="text-xs">
                              Likelihood: {risk.likelihood}
                            </Badge>
                          )}
                          {risk.impact && (
                            <Badge variant="outline" className="text-xs">
                              Impact: {risk.impact}
                            </Badge>
                          )}
                        </div>
                        {risk.mitigation && (
                          <p className="text-sm text-slate-600 mt-2">
                            <strong>Mitigation:</strong> {risk.mitigation}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic py-8 text-center">
                    No risks extracted
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tracked Actions Tab */}
          <TabsContent value="tracked" className="space-y-4">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-600">
                  Tracked Actions from This Meeting
                </CardTitle>
              </CardHeader>
              <CardContent>
                {actions.length > 0 ? (
                  <div className="space-y-3">
                    {actions.map((action) => (
                      <div key={action.id} className="border border-slate-200 rounded-sm p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs text-slate-500">{action.action_number}</span>
                              <Badge className={`text-xs ${STATUS_COLORS[action.status] || ''}`}>
                                {action.status}
                              </Badge>
                              <Badge className={`text-xs ${PRIORITY_COLORS[action.priority] || ''}`}>
                                {action.priority}
                              </Badge>
                            </div>
                            <p className="font-medium text-slate-900 mt-1">{action.task_title}</p>
                            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                              {action.assigned_to_name && (
                                <span>
                                  <User className="h-3 w-3 inline mr-1" />
                                  {action.assigned_to_name}
                                </span>
                              )}
                              <span>
                                <Building2 className="h-3 w-3 inline mr-1" />
                                {action.assigned_to_party}
                              </span>
                              {action.due_date && (
                                <span>
                                  <Calendar className="h-3 w-3 inline mr-1" />
                                  {formatDate(action.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Select
                              value={action.status}
                              onValueChange={(value) => handleUpdateActionStatus(action.id, value)}
                            >
                              <SelectTrigger className="w-[130px] h-8 text-xs rounded-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Open">Open</SelectItem>
                                <SelectItem value="In Progress">In Progress</SelectItem>
                                <SelectItem value="Awaiting Response">Awaiting Response</SelectItem>
                                <SelectItem value="Blocked">Blocked</SelectItem>
                                <SelectItem value="Closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConvertToRFI(action.id)}
                              className="h-8 text-xs rounded-sm"
                              title="Convert to RFI"
                            >
                              <FileQuestion className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleConvertToDelayEvent(action.id)}
                              className="h-8 text-xs rounded-sm"
                              title="Convert to Delay Event"
                            >
                              <AlertTriangle className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic py-8 text-center">
                    No tracked actions yet. Create tasks from the Actions tab.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Correspondence Tab */}
          <TabsContent value="correspondence">
            <CorrespondenceTab 
              projectId={minutes?.project_id}
              moduleType="mm"
              moduleEntityId={minutesId}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default MeetingMinutesDetail;
